import React from 'react';
import PropTypes from 'prop-types';
import MaterialTable, { MTableBody } from 'material-table';
import { TablePagination, TableCell } from '@material-ui/core';
import { MUIDatatablesTranslations } from 'constants/translations';
import Immutable from 'seamless-immutable';
import FooterTable from 'components/Tables/DefaultTable/FooterTable';
import BannerFooter from 'components/Pages/BannerPage/BannerFooter';
import Grid from '@material-ui/core/Grid';

const DefaultTable = ({
  getFunction,
  perPage,
  page,
  columns,
  total,
  data,
  isLoading,
  orderByDirection,
  orderByColumn,
  footer,
  showLegend = true,
  sorting = true,
  disablePagination = false,
}) => {
  const [tableData, setTableData] = React.useState([]);

  const loadData = (
    pageL = 1,
    perPageL = perPage,
    orderByColumnL = orderByColumn,
    orderByDirectionL = orderByDirection,
  ) => {
    getFunction({
      page: pageL,
      perPage: perPageL,
      orderByColumn: orderByColumnL,
      orderByDirection: orderByDirectionL,
    });
  };

  const onChangePage = (event, requestPage) => {
    loadData(requestPage + 1, perPage);
  };

  const onChangeRowsPerPage = event => {
    const { value } = event.target;
    loadData(1, value);
  };

  React.useEffect(() => {
    const dataToSet = Immutable.asMutable(data, { deep: true });
    setTableData(dataToSet);
  }, [data]);

  const onOrderChangeCall = (columnIndex, direction) => {
    if (
      columnIndex >= 0 &&
      columns[columnIndex].field &&
      typeof columns[columnIndex].field === 'string'
    ) {
      loadData(1, perPage, columns[columnIndex].field, direction);
    } else {
      loadData(1, perPage, '', '');
    }
  };

  return (
    <div style={{ maxWidth: '99.99%' }}>
      <MaterialTable
        localization={MUIDatatablesTranslations}
        isLoading={isLoading}
        data={data}
        columns={columns}
        options={{
          filtering: false,
          search: false,
          sorting,
          showTitle: false,
          toolbar: false,
          draggable: false,
          headerStyle: {
            color: 'black',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            fontSize: 15,
          },
          pageSize: perPage,
        }}
        onOrderChange={onOrderChangeCall}
        components={{
          Pagination: props => (
            <FooterTable footer={footer}>
              <Grid container spacing={3}>
                <Grid item xs>
                  {showLegend && <BannerFooter />}
                </Grid>
                <Grid item xs>
                  {!disablePagination && (
                    <TablePagination
                      {...props}
                      page={page - 1}
                      rowsPerPage={perPage}
                      count={total}
                      rowsPerPageOptions={[5, 10]}
                      onChangePage={onChangePage}
                      component={footer !== null ? 'div' : TableCell}
                      onChangeRowsPerPage={onChangeRowsPerPage}
                    />
                  )}
                </Grid>
              </Grid>
            </FooterTable>
          ),
          Body: ({ renderData, ...subProps }) => {
            let dataToRender = renderData;
            if (
              tableData.length > 0 &&
              tableData[0].id &&
              renderData.length > 0 &&
              renderData.find(rd => rd.id === tableData[0].id)
            ) {
              dataToRender = tableData.map(d => {
                const renderDataItem = renderData.find(rd => rd.id === d.id);
                return renderDataItem;
              });
            }
            return (
              <>
                <MTableBody
                  {...subProps}
                  options={{ ...subProps.options, paging: false }} // set paging:false to avoid array slice
                  currentPage={page}
                  renderData={dataToRender}
                  pageSize={perPage}
                />
              </>
            );
          },
        }}
      />
    </div>
  );
};

DefaultTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  getFunction: PropTypes.func,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  total: PropTypes.number.isRequired,
  page: PropTypes.number,
  perPage: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  footer: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string,
  ]),
  orderByDirection: PropTypes.string,
  orderByColumn: PropTypes.string,
  showLegend: PropTypes.bool,
};

DefaultTable.defaultProps = {
  getFunction: () => {},
  // onOrderChange: () => {},
  columns: [],
  page: 1,
  perPage: 10,
  footer: null,
  orderByDirection: '',
  orderByColumn: 'name',
  showLegend: true,
};

export default DefaultTable;
